import React from 'react';

const statusStyles = {
    // Generic statuses
    active: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
    inactive: 'bg-slate-50 text-slate-600 ring-1 ring-slate-200',
    pending: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',

    // Transaction statuses
    success: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
    successful: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
    completed: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
    failed: 'bg-red-50 text-red-700 ring-1 ring-red-200',
    cancelled: 'bg-slate-50 text-slate-600 ring-1 ring-slate-200',
    refunded: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',

    // Game statuses
    upcoming: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
    ongoing: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
    live: 'bg-red-50 text-red-700 ring-1 ring-red-200',
    finished: 'bg-slate-50 text-slate-600 ring-1 ring-slate-200',

    // User statuses
    verified: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
    unverified: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
    banned: 'bg-red-50 text-red-700 ring-1 ring-red-200',
    pro: 'bg-purple-50 text-purple-700 ring-1 ring-purple-200',

    // Approval statuses
    approved: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
    rejected: 'bg-red-50 text-red-700 ring-1 ring-red-200',
    review: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
    info: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
    purple: 'bg-purple-50 text-purple-700 ring-1 ring-purple-200',
    danger: 'bg-red-50 text-red-700 ring-1 ring-red-200',
    warning: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
    primary: 'bg-primary/5 text-primary ring-1 ring-primary/20',

    // Default
    default: 'bg-slate-50 text-slate-600 ring-1 ring-slate-200',
} as const;

export type StatusVariant = keyof typeof statusStyles;
type StatusType = StatusVariant | string;

interface StatusBadgeProps {
    status: StatusType;
    label?: React.ReactNode;
    variant?: StatusVariant;
    size?: 'xs' | 'sm' | 'md';
    className?: string;
}

const formatStatusLabel = (value: unknown): React.ReactNode => {
    if (React.isValidElement(value)) return value;
    if (typeof value === 'string' || typeof value === 'number') return value;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value && typeof value === 'object') {
        const record = value as Record<string, unknown>;
        const typeValue = record.type;
        if (typeof typeValue === 'string' && typeValue.trim()) return typeValue;
        const availableValue = record.available;
        if (typeof availableValue === 'boolean') {
            return availableValue ? 'Available' : 'Unavailable';
        }
        return JSON.stringify(value);
    }
    return '';
};

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, label, variant, size = 'sm', className = '' }) => {
    const rawStatus = (typeof status === 'string' || typeof status === 'number')
        ? status
        : ((typeof label === 'string' || typeof label === 'number') ? label : 'default');
    const normalizedStatus = String(rawStatus || 'default').toLowerCase().replace(/\s+/g, '');
    const statusVariant: StatusVariant = (normalizedStatus in statusStyles)
        ? (normalizedStatus as StatusVariant)
        : 'default';
    const style = variant ? statusStyles[variant] : statusStyles[statusVariant];
    const displayLabel = formatStatusLabel(label ?? status);

    const sizes = {
        xs: 'px-1.5 py-0.5 text-[10px]',
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-1 text-sm',
    };

    return (
        <span
            className={`
        inline-flex items-center font-medium rounded-full
        ${style}
        ${sizes[size]}
        ${className}
      `}
        >
            {displayLabel}
        </span>
    );
};

export default StatusBadge;
